const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/utils/dateTime/dateTime.ts';
import moment, { Moment } from 'moment';
import {
    KISIM_STD_DATE_TIME_FORMAT,
    KISIM_STD_TIMEONLY_FORMAT,
    KISIM_TIME_FORMAT,
    KISIM_TIME_LOWER_LIMIT,
} from '../../defs/generalDefs';
import textMap from './translations';
import { TextInput } from '../intlUtils';
import { useIntl } from 'react-intl';
import DurationConstructor = moment.unitOfTime.DurationConstructor;

export const time_alpha = moment(KISIM_TIME_LOWER_LIMIT);
export const time_omega = moment('3000-12-31 23:59:59');
export const getNowPlus = (amount: number, unit: DurationConstructor) => moment().add(amount, unit);
export const getNowMinus = (amount: number, unit: DurationConstructor) => moment().subtract(amount, unit);
export const getTodayMinus = (amount: number, unit: DurationConstructor = 'days') =>
    moment().startOf('day').subtract(amount, unit);
export const getStartOfToday = () => moment().startOf('day');
export const getEndOfToday = () => moment().endOf('day');

export const isToday = (input: Moment): boolean => input.isSame(moment(), 'day');

/**
 * The (optional) stop date must be strictly after the start date. An empty stop date means open-ended and is
 * allowed. Returns an error message (as a MessageDescriptor) when invalid, else undefined.
 */
export const validateStopAfterStart = (stopDate: string, startDate: string): TextInput => {
    if (!stopDate) {
        return undefined;
    }
    return moment(stopDate, KISIM_TIME_FORMAT).isAfter(moment(startDate, KISIM_TIME_FORMAT))
        ? undefined
        : textMap.stopBeforeStartError;
};

export const formatStandard = (input?: string | Moment, parseFormat: string = KISIM_TIME_FORMAT) => {
    const theMoment = typeof input === 'string' ? moment(input, parseFormat) : (input as Moment);
    return theMoment.format(KISIM_STD_DATE_TIME_FORMAT);
};

export const formatRelativeToToday = (
    input?: string | Moment,
    parseFormat: string = KISIM_TIME_FORMAT,
    dayMonthFormat = 'D MMM',
    dayOffsetToIncludeTime = 3
) => {
    const theMoment = typeof input === 'string' ? moment(input, parseFormat) : (input as Moment);
    if (theMoment.isBetween(getStartOfToday(), getEndOfToday())) {
        return theMoment.format(KISIM_STD_TIMEONLY_FORMAT);
    }

    if (theMoment.isSame(moment(), 'year')) {
        const lowerLimitToIncludeTime = moment().subtract(dayOffsetToIncludeTime, 'days').startOf('day');
        const upperLimitToIncludeTime = moment().add(dayOffsetToIncludeTime, 'days').endOf('day');
        const format =
            theMoment.isAfter(lowerLimitToIncludeTime) && theMoment.isBefore(upperLimitToIncludeTime)
                ? `${dayMonthFormat} ${KISIM_STD_TIMEONLY_FORMAT}`
                : dayMonthFormat;
        return theMoment.format(format);
    }

    return theMoment.format(`${dayMonthFormat} YYYY`);
};

// 44, 1:14, or 30:05 etc
export const getDifferenceInHoursAndMinutes = (moment: Moment, referenceMoment: Moment, alwaysShowHour?: boolean) => {
    const difference = referenceMoment.diff(moment, 'hours', true);
    // when difference is negative, moment is before reference
    const isBefore = difference < 0;
    const hours = isBefore ? Math.ceil(difference) : Math.floor(difference);
    const minutes = Math.abs(Math.floor((difference - hours) * 60));
    const minutesTextArray = [minutes.toString(10)];
    if (minutes < 10 && (hours !== 0 || alwaysShowHour)) minutesTextArray.unshift('0');
    if (isBefore && hours === 0 && !alwaysShowHour) minutesTextArray.unshift('-');
    const minutesText = minutesTextArray.join('');
    return hours || alwaysShowHour ? `${isBefore ? '-' : ''}${Math.abs(hours)}:${minutesText}` : `${minutesText}`;
};

export const useDurationText = (startDate: string, endDate: string) => {
    const { formatMessage } = useIntl();

    const duration = moment.duration(moment(endDate).diff(startDate));
    const minutes = duration.minutes();
    const hours = duration.hours();
    if (hours === 1 && minutes === 1) {
        return formatMessage(textMap['hoursAndMinutes.OneOne'], { hours, minutes });
    } else if (hours === 1 && minutes) {
        return formatMessage(textMap['hoursAndMinutes.OneMany'], { hours, minutes });
    } else if (hours && minutes === 1) {
        return formatMessage(textMap['hoursAndMinutes.ManyOne'], { hours, minutes });
    } else if (hours && minutes) {
        return formatMessage(textMap['hoursAndMinutes.ManyMany'], { hours, minutes });
    } else if (hours) {
        return formatMessage(textMap.hours, { hours });
    } else {
        return formatMessage(textMap.minutes, { minutes });
    }
};
